import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box, CircularProgress } from '@mui/material';

import useHlsSource from '../../hooks/useHlsSource';
import { Video } from '../../types';
import styles from './style';

// Only a few seconds ahead: a phone keeps 2 players at once.
const HLS_CONFIG = {
  capLevelToPlayerSize: true,
  maxBufferLength: 8,
  maxMaxBufferLength: 12,
  backBufferLength: 10,
  // Plays a segment while it downloads, like the app's player: old videos have segments up to 8 s.
  progressive: true,
};
// The next video only loads its start: the one on screen gets the network.
const NEIGHBOUR_BUFFER = 2;

interface MobileFeedPlayerProps {
  index: number;
  video: Video;
  current: boolean;
  playing: boolean;
  muted: boolean;
  // The browser refused the sound: the feed goes muted.
  onForcedMute: () => void;
  // The feed starts the video itself when the finger lifts (see MobileFeed).
  onElement: (index: number, el: HTMLVideoElement | null) => void;
}

// The video of one slide of the mobile feed. Only the slides next to the screen have one: the
// others are unmounted and give their memory back.
const MobileFeedPlayer: React.FC<MobileFeedPlayerProps> = React.memo((props) => {
  const {
    index,
    video,
    current,
    playing,
    muted,
    onForcedMute,
    onElement,
  } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  const playingRef = useRef(playing);
  playingRef.current = playing;
  const leftRef = useRef(false);
  const [buffering, setBuffering] = useState(true);

  const hlsRef = useHlsSource(videoRef, video, HLS_CONFIG);

  useEffect(() => {
    onElement(index, videoRef.current);
    return () => onElement(index, null);
  }, [index, onElement]);

  const tryPlay = useCallback(() => {
    const el = videoRef.current;
    if (!el || !playingRef.current || !el.paused) {
      return;
    }
    el.play().catch((error) => {
      // No sound allowed yet: the video still starts, muted.
      if (error && error.name === 'NotAllowedError' && !el.muted) {
        el.muted = true;
        onForcedMute();
        el.play().catch(() => undefined);
      }
    });
  }, [onForcedMute]);

  useEffect(() => {
    if (hlsRef.current) {
      hlsRef.current.config.maxBufferLength = current ? HLS_CONFIG.maxBufferLength : NEIGHBOUR_BUFFER;
    }
  }, [current, hlsRef]);

  // Rewound when it comes back, not when it leaves: the seek would download its start again
  // while the next video loads.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) {
      return;
    }
    if (!current) {
      leftRef.current = el.currentTime > 0;
    } else if (leftRef.current) {
      leftRef.current = false;
      el.currentTime = 0;
    }
  }, [current]);

  // Before the play below: the video on screen starts with the sound of the feed, not muted then unmuted.
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted || !current;
    }
  }, [current, muted]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) {
      return;
    }
    if (playing) {
      tryPlay();
    } else {
      el.pause();
    }
  }, [playing, tryPlay]);

  const onWaiting = useCallback(() => setBuffering(true), []);
  const onPlaying = useCallback(() => setBuffering(false), []);

  return (
    <>
      <Box
        component="video"
        ref={videoRef}
        loop
        muted
        playsInline
        // Old name of playsInline, still read by some in-app browsers (TikTok, Instagram).
        webkit-playsinline="true"
        preload="auto"
        disablePictureInPicture
        disableRemotePlayback
        onCanPlay={tryPlay}
        onWaiting={onWaiting}
        onPlaying={onPlaying}
        sx={video.width > video.height ? styles.videoLandscape : styles.video}
      />
      {current && playing && buffering && <CircularProgress size={34} sx={styles.buffering} />}
    </>
  );
});

export default MobileFeedPlayer;
